import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import makeStyles from "@mui/styles/makeStyles";
import { FACING_MODES, IMAGE_TYPES } from "jslib-html5-camera-photo";

import { useLibCameraPhoto } from "./hooks/useLibCameraPhoto";

import WhiteFlash from "../WhiteFlash";
import DisplayError from "../DisplayError";
import {
    getShowHideStyle,
    getVideoStyles,
    playClickAudio,
    printCameraInfo,
} from "./helpers";
import styles from "./styles";

import { Box, CircularProgress, Grid, IconButton } from "@mui/material/";
import FlashAutoIcon from "@mui/icons-material/FlashAuto";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import "./styles/camera.css";

let showVideoTimeoutId = null;

/*
Inspiration : https://www.html5rocks.com/en/tutorials/getusermedia/intro/
*/
function Camera(props) {
    const [dataUri, setDataUri] = useState("");
    const [loading, setLoading] = useState(true);
    const [isShowVideo, setIsShowVideo] = useState(true);
    const [cameraFlash, setCameraFlash] = useState(false);
    const [vertical, setIsVertical] = useState(
        window.innerWidth < window.innerHeight
    );
    const [cameraStartDisplayError, setCameraStartDisplayError] = useState("");
    const classes = makeStyles(styles)();

    let videoRef = useRef(null);

    const [mediaStream, cameraStartError, cameraStopError, getDataUri] =
        useLibCameraPhoto(
            videoRef,
            FACING_MODES.ENVIRONMENT,
            props.idealResolution,
            props.isMaxResolution
        );

    /**
     * this useEffect is used to dynamically calculate width and left offset for the
     * image-overlay to make it aligned with video streaming
     */

    useEffect(() => {
        if (!props.overlay) return;

        let videoStream = document.getElementById("video-stream");
        if (!videoStream) return;

        function calculateWidth() {
            let video = document.querySelector("video");

            if (!video) return;

            setIsVertical(window.innerWidth < window.innerHeight);

            const ratio = video.videoWidth / video.videoHeight;

            const overlayHeight =
                document.getElementById("image-overlay").offsetHeight;

            const videoPreviewWidth =
                document.getElementById("video-grid-item").offsetWidth;
            const newOverlayWidth = overlayHeight * ratio;

            document.getElementById(
                "image-overlay"
            ).style.width = `${newOverlayWidth}px`;

            document.getElementById("image-overlay").style.left = `${
                (videoPreviewWidth - video.offsetWidth) / 2
            }px`;
        }

        window.addEventListener("resize", calculateWidth);
        videoStream.addEventListener("resize", calculateWidth);

        return () => {
            window.removeEventListener("resize", calculateWidth);
            videoStream.addEventListener("resize", calculateWidth);
        };
    }, []);

    useEffect(() => {
        if (mediaStream) {
            if (typeof props.onCameraStart === "function") {
                props.onCameraStart(mediaStream);
                setLoading(!Boolean(mediaStream));
            }
        } else {
            if (typeof props.onCameraStop === "function") {
                props.onCameraStop();
            }
        }
    }, [mediaStream]);

    useEffect(() => {
        if (cameraStartError) {
            setCameraStartDisplayError(
                `${cameraStartError.name} ${cameraStartError.message}`
            );
            if (typeof props.onCameraError === "function") {
                props.onCameraError(cameraStartError);
            }
        }
    }, [cameraStartError]);

    useEffect(() => {
        if (cameraStopError) {
            printCameraInfo(cameraStopError.message);
        }
    }, [cameraStopError]);

    function clearShowVideoTimeout() {
        if (showVideoTimeoutId) {
            clearTimeout(showVideoTimeoutId);
        }
    }

    function getIsImageMirror() {
        if (props.isImageMirror !== undefined) {
            return props.isImageMirror;
        }

        // TODO: When we get a camera id detect if is a facing mode USER or ENVIRONMENT
        if (props.idealFacingMode === FACING_MODES.USER) {
            return true;
        }

        // By default and if is FACING_MODE.ENVIRONMENT is false
        return false;
    }

    const toggleFlash = () => {
        const track = mediaStream.getTracks()[0];
        const imageCapture = new ImageCapture(track);

        imageCapture.getPhotoCapabilities().then(() => {
            //TODO: check if camera has a torch
            track.applyConstraints({
                advanced: [{ torch: !cameraFlash }],
            });
        });

        setCameraFlash(!cameraFlash);
    };

    function handleTakePhoto() {
        const configDataUri = {
            sizeFactor: props.sizeFactor,
            imageType: props.imageType,
            imageCompression: props.imageCompression,
            isImageMirror: getIsImageMirror(),
        };

        let dataUri = getDataUri(configDataUri);

        if (!props.isSilentMode) {
            playClickAudio();
        }

        if (typeof props.onTakePhoto === "function") {
            props.onTakePhoto(dataUri);
        }

        setDataUri(dataUri);
        setIsShowVideo(false);

        clearShowVideoTimeout();
        showVideoTimeoutId = setTimeout(() => {
            setIsShowVideo(true);

            if (typeof props.onTakePhotoAnimationDone === "function") {
                props.onTakePhotoAnimationDone(dataUri);
            }
        }, 900);
    }

    let videoStyles = getVideoStyles(isShowVideo, getIsImageMirror());

    const videoPreview = useMemo(() => {
        return (
            <Grid id="video-grid-item" item xs={12} md={8} sm={8}>
                <div id="video_overlay" className={classes.videoOverlay}>
                    {loading ? (
                        <Box className={classes.loading}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {props.overlay && (
                                <Box
                                    component="img"
                                    id="image-overlay"
                                    alt="realtime-image"
                                    src={props.overlay}
                                    className={classes.overlayImage}
                                />
                            )}
                        </>
                    )}
                    <DisplayError
                        cssClass={"display-error"}
                        isDisplayError={props.isDisplayStartCameraError}
                        errorMsg={cameraStartDisplayError}
                    />
                    <WhiteFlash isShowWhiteFlash={!isShowVideo} />

                    <video
                        id="video-stream"
                        style={videoStyles}
                        ref={videoRef}
                        autoPlay={true}
                        muted={true}
                        playsInline
                        width={vertical ? `${window.innerWidth - 32}` : "auto"}
                        height={
                            !vertical
                                ? `${window.innerHeight - 32 - 64}`
                                : "auto"
                        }
                        className={classes.videoStream}
                    />
                </div>
            </Grid>
        );
    }, [classes, mediaStream, vertical, loading]);

    const RenderVideoControls = () => {
        return (
            <Grid item container xs={12} md={4} sm={4}>
                <Grid
                    item
                    xs={3}
                    md={12}
                    sm={12}
                    className={classes.centeredGrid}
                >
                    <IconButton
                        color={cameraFlash ? "primary" : "secondary"}
                        aria-label="toggle flash"
                        component="span"
                        onClick={toggleFlash}
                    >
                        <FlashAutoIcon className={classes.flashIcon} />
                    </IconButton>
                </Grid>
                <Grid
                    item
                    xs={6}
                    md={12}
                    sm={12}
                    className={classes.centeredGrid}
                >
                    <IconButton
                        color="primary"
                        aria-label="capture image"
                        component="span"
                        onClick={handleTakePhoto}
                    >
                        <PanoramaFishEyeIcon className={classes.fishEyeIcon} />
                    </IconButton>
                </Grid>
                <Grid item xs={3} md={12} sm={12}></Grid>
            </Grid>
        );
    };

    return (
        <Grid container className={classes.grid}>
            {videoPreview}
            {!loading && <RenderVideoControls />}
        </Grid>
    );
}

export { Camera, FACING_MODES, IMAGE_TYPES };

export default Camera;

Camera.propTypes = {
    overlay: PropTypes.string,
    onTakePhoto: PropTypes.func,
    onTakePhotoAnimationDone: PropTypes.func,
    onCameraError: PropTypes.func,
    idealFacingMode: PropTypes.string,
    idealResolution: PropTypes.object,
    imageType: PropTypes.string,
    isImageMirror: PropTypes.bool,
    isSilentMode: PropTypes.bool,
    isDisplayStartCameraError: PropTypes.bool,
    imageCompression: PropTypes.number,
    isMaxResolution: PropTypes.bool,
    isFullscreen: PropTypes.bool,
    sizeFactor: PropTypes.number,
    onCameraStart: PropTypes.func,
    onCameraStop: PropTypes.func,
};

Camera.defaultProps = {
    isDisplayStartCameraError: true,
};
